import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useQualityStore from '../store/qualityStore';
import useProjectStore from '../store/projectStore';
import useAuthStore from '../store/authStore';
import useUIStore from '../store/uiStore';
import { getEmployees } from '../services/employeeService';
import { getProjectStages } from '../services/projectService';
import { formatDateDDMMYY } from '../utils/formatDate';
import ScrollX from '../components/ScrollX';

const statusColors = {
  pending: 'bg-gray-200 text-gray-800',
  passed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  signed_off: 'bg-blue-100 text-blue-800',
};

const initialForm = {
  project_id: '', inspection_type: 'intermediate', inspector_id: '', status: 'pending', findings: '', project_stage_id: '',
};

// The checklist confirmation embedded in an inspection - who on production
// checked each item, and whether quality has verified it yet. Read-only
// here (checking happens on Quality > Checklists or inline in Daily Entry) -
// this is about seeing what led up to the call, not re-doing it.
const ChecklistConfirmation = ({ confirmation }) => {
  if (!confirmation) return null;
  return (
    <div className="mt-2 border border-gray-200 rounded">
      <div className="px-3 py-1.5 text-xs font-semibold text-gray-600 bg-gray-50 border-b border-gray-100">
        Checklist "{confirmation.checklist_name}"
      </div>
      <div className="divide-y divide-gray-100">
        {confirmation.items.map((item) => (
          <div key={item.id} className="px-3 py-1.5 text-xs flex items-center justify-between gap-2">
            <span>
              {item.description}
              {item.is_critical && <span className="ml-1 text-red-600 font-semibold">(critical)</span>}
            </span>
            <span className="text-gray-400 whitespace-nowrap">
              {item.production_checked ? `✓ ${item.production_checked_by || 'production'}` : '— not checked'}
              {' · '}
              {item.quality_checked ? `✓ ${item.quality_checked_by || 'quality'}` : 'not verified'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const InspectionCard = ({ inspection: i, onMarkResult, onSignOff, onRecall, onEdit, onRaiseNCR }) => (
  <div className={`border rounded p-3 ${i.status === 'failed' ? 'border-red-200 bg-red-50' : 'border-gray-200'}`}>
    <div className="flex items-center justify-between flex-wrap gap-2">
      <div className="text-sm">
        <span className="font-medium">{i.project_name}</span>
        {i.project_stage_name && <span className="text-gray-400"> · {i.project_stage_name}</span>}
        <span className="text-gray-400"> · {i.inspection_type} · {formatDateDDMMYY(i.inspection_date)}</span>
      </div>
      <span className={`text-xs px-2 py-1 rounded font-semibold ${statusColors[i.status]}`}>
        {i.status.replace('_', ' ')}
      </span>
    </div>
    <div className="text-xs text-gray-500 mt-1">Inspector: {i.inspector_name || '—'}</div>
    {i.findings && <div className="text-xs text-gray-600 mt-1 italic">"{i.findings}"</div>}

    <ChecklistConfirmation confirmation={i.checklist_confirmation} />

    <div className="flex items-center gap-2 mt-2 flex-wrap">
      {i.status === 'pending' && (
        <>
          <button onClick={() => onMarkResult(i, 'passed')} className="text-xs px-2 py-1 rounded bg-green-600 text-white font-semibold">
            Mark Passed
          </button>
          <button onClick={() => onMarkResult(i, 'failed')} className="text-xs px-2 py-1 rounded bg-red-600 text-white font-semibold">
            Mark Failed
          </button>
        </>
      )}
      {i.status === 'passed' && (
        <>
          <button onClick={() => onSignOff(i)} className="text-xs px-2 py-1 rounded bg-komal-blue text-white font-semibold">
            Sign Off
          </button>
          <button onClick={() => onRecall(i)} className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-700 font-semibold">
            Recall to Pending
          </button>
        </>
      )}
      {i.status === 'failed' && (
        <>
          <button onClick={() => onRaiseNCR(i)} className="text-xs px-2 py-1 rounded bg-red-600 text-white font-semibold">
            Raise NCR
          </button>
          <button onClick={() => onRecall(i)} className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-700 font-semibold">
            Recall to Pending
          </button>
        </>
      )}
      {i.status !== 'signed_off' && (
        <button onClick={() => onEdit(i)} className="text-xs text-white hover:underline">
          Edit findings
        </button>
      )}
      {i.status === 'signed_off' && (
        <button onClick={() => onEdit(i)} className="text-xs text-white hover:underline">
          Edit findings (record correction only)
        </button>
      )}
    </div>
  </div>
);

const InspectionsPage = () => {
  const {
    inspections, isLoading, error, fetchInspections, createInspection,
    signOffInspection, setInspectionResult, editInspection,
  } = useQualityStore();
  const { projects, fetchProjects } = useProjectStore();
  const { user } = useAuthStore();
  const showToast = useUIStore((s) => s.showToast);
  const navigate = useNavigate();

  const [employees, setEmployees] = useState([]);
  const [stages, setStages] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [showCreateForm, setShowCreateForm] = useState(false);

  useEffect(() => {
    fetchInspections();
    fetchProjects();
    getEmployees().then((res) => setEmployees(res.data.employees || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (error) showToast(error, 'error');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    if (!form.project_id) {
      setStages([]);
      return;
    }
    getProjectStages(form.project_id)
      .then((res) => setStages((res.data.stages || []).filter((s) => s.requires_inspection)))
      .catch(() => setStages([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.project_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.project_id || !form.inspector_id) {
      showToast('Project and inspector are required', 'error');
      return;
    }
    const result = await createInspection({
      ...form,
      project_id: Number(form.project_id),
      inspector_id: Number(form.inspector_id),
      project_stage_id: form.project_stage_id ? Number(form.project_stage_id) : null,
    });
    if (result.success) {
      showToast('Inspection recorded', 'success');
      setForm(initialForm);
    } else {
      showToast(result.error, 'error');
    }
  };

  const handleMarkResult = async (inspection, resultStatus) => {
    const findings = window.prompt(
      `Findings for this ${resultStatus === 'failed' ? 'failure' : 'pass'} (optional):`,
      inspection.findings || ''
    );
    if (findings === null) return;
    const result = await setInspectionResult(inspection.id, resultStatus, findings || undefined);
    if (result.success) showToast(`Marked ${resultStatus}`, 'success');
  };

  const handleRecall = async (inspection) => {
    if (!window.confirm(`Recall this ${inspection.status} result back to Pending? Use this if the wrong result was recorded.`)) return;
    const result = await setInspectionResult(inspection.id, 'pending');
    if (result.success) showToast('Recalled to pending', 'success');
  };

  const handleEdit = async (inspection) => {
    const findings = window.prompt('Correct the findings text:', inspection.findings || '');
    if (findings === null) return;
    const result = await editInspection(inspection.id, { findings });
    if (result.success) showToast('Findings updated', 'success');
  };

  const handleSignOff = async (inspection) => {
    const comments = window.prompt('Sign-off comments (optional):') || '';
    const result = await signOffInspection(inspection.id, { signed_by_id: user?.id, comments });
    if (result.success) showToast('Inspection signed off', 'success');
    else showToast(result.error, 'error');
  };

  const handleRaiseNCR = (inspection) => {
    navigate(`/ncr/create?project_id=${inspection.project_id}&inspection_id=${inspection.id}&description=${encodeURIComponent(inspection.findings || '')}`);
  };

  const needsAction = inspections.filter((i) => i.status !== 'signed_off');
  const closed = inspections.filter((i) => i.status === 'signed_off');

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-xl font-bold text-white mb-1">Inspections</h1>
        <p className="text-sm text-gray-500 mb-6">
          What production checked off, and the verification calls waiting on you.
        </p>

        <div className="glass-card p-6 mb-6">
          <h2 className="font-semibold text-gray-900 mb-4">Needs Action ({needsAction.length})</h2>
          {isLoading ? (
            <p className="text-gray-500 text-sm">Loading...</p>
          ) : needsAction.length === 0 ? (
            <p className="text-gray-400 text-sm">Nothing waiting on you right now.</p>
          ) : (
            <div className="space-y-3">
              {needsAction.map((i) => (
                <InspectionCard
                  key={i.id}
                  inspection={i}
                  onMarkResult={handleMarkResult}
                  onSignOff={handleSignOff}
                  onRecall={handleRecall}
                  onEdit={handleEdit}
                  onRaiseNCR={handleRaiseNCR}
                />
              ))}
            </div>
          )}
        </div>

        <div className="glass-card p-6 mb-6">
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="text-sm font-semibold text-komal-blue hover:underline"
          >
            {showCreateForm ? 'Hide' : '+ Record an inspection manually'}
          </button>
          <p className="text-xs text-gray-400 mt-1">
            Most inspections start from "Call Inspection" on a project's Production Plan - use this only for one-off inspections not tied to a stage.
          </p>

          {showCreateForm && (
            <form onSubmit={handleSubmit} className="space-y-4 mt-4 pt-4 border-t border-gray-100">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Project *</label>
                  <select
                    value={form.project_id}
                    onChange={(e) => setForm({ ...form, project_id: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  >
                    <option value="">Select a project</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Inspector *</label>
                  <select
                    value={form.inspector_id}
                    onChange={(e) => setForm({ ...form, inspector_id: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  >
                    <option value="">Select an inspector</option>
                    {employees.map((e) => <option key={e.id} value={e.id}>{e.name} — {e.role}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={form.inspection_type}
                    onChange={(e) => setForm({ ...form, inspection_type: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  >
                    <option value="intermediate">Intermediate</option>
                    <option value="FAT">FAT</option>
                    <option value="SAT">SAT</option>
                    <option value="final">Final</option>
                  </select>
                </div>
                {stages.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Production Stage</label>
                    <select
                      value={form.project_stage_id}
                      onChange={(e) => setForm({ ...form, project_stage_id: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    >
                      <option value="">Not tied to a stage</option>
                      {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Result</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  >
                    <option value="pending">Pending</option>
                    <option value="passed">Passed</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Findings</label>
                <textarea
                  rows={2}
                  value={form.findings}
                  onChange={(e) => setForm({ ...form, findings: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <button type="submit" className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold">
                Record Inspection
              </button>
            </form>
          )}
        </div>

        <div className="glass-card">
          <div className="px-5 py-4 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900">Signed Off ({closed.length})</h2>
          </div>
          {closed.length === 0 ? (
            <p className="text-gray-500 text-sm p-5">No signed-off inspections yet.</p>
          ) : (
            <ScrollX>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="px-5 py-2 font-medium">Project</th>
                  <th className="px-5 py-2 font-medium">Type</th>
                  <th className="px-5 py-2 font-medium">Findings</th>
                  <th className="px-5 py-2 font-medium">Signed Off</th>
                  <th className="px-5 py-2 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {closed.map((i) => (
                  <tr key={i.id} className="border-b border-gray-50 last:border-0">
                    <td className="px-5 py-3">{i.project_name}{i.project_stage_name && ` · ${i.project_stage_name}`}</td>
                    <td className="px-5 py-3">{i.inspection_type}</td>
                    <td className="px-5 py-3 max-w-xs truncate">{i.findings || '—'}</td>
                    <td className="px-5 py-3 text-gray-500">{formatDateDDMMYY(i.sign_off_date)}</td>
                    <td className="px-5 py-3">
                      <button onClick={() => handleEdit(i)} className="text-komal-blue hover:underline text-xs font-semibold">
                        Edit findings
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </ScrollX>
          )}
        </div>
      </div>
    </div>
  );
};

export default InspectionsPage;
